import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Plus, Search } from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { CandidateModal } from '../components/crm/CandidateModal';
import { PlacementModal } from '../components/crm/PlacementModal';
import { PIPELINE_STAGES, stageMeta, candidateName } from '../components/crm/constants';

export default function SalesCandidates() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [placementModal, setPlacementModal] = useState({ open: false, candidate: null });

  const load = useCallback(async () => {
    const res = await crmFetch('/candidates');
    if (res.tableMissing) setTableMissing(true);
    else if (res.success) { setCandidates(res.data || []); setTableMissing(false); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const moveStage = async (candidate, stage) => {
    if (stage === candidate.stage) return;
    const res = await crmFetch(`/candidates/${candidate.id}`, { method: 'PATCH', body: JSON.stringify({ stage }) });
    if (res.success) {
      toast.success(`Moved to ${stageMeta(stage).label}`);
      if (stage === 'placement') setPlacementModal({ open: true, candidate: { ...candidate, stage } });
      load();
    } else toast.error(res.message || 'Failed to move candidate');
  };

  const counts = {};
  for (const c of candidates) counts[c.stage] = (counts[c.stage] || 0) + 1;

  const q = search.trim().toLowerCase();
  const filtered = candidates.filter((c) => {
    if (stageFilter !== 'all' && c.stage !== stageFilter) return false;
    if (!q) return true;
    return [candidateName(c), c.jobTitle, c.accountName, c.current_company].some((v) => (v || '').toLowerCase().includes(q));
  });

  const STAGE_FILTERS = [{ key: 'all', label: 'All' }, ...PIPELINE_STAGES.map((s) => ({ key: s.value, label: s.label }))];

  return (
    <SalesLayout>
      <div data-testid="candidates-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Candidates</h1>
            <p className="text-sm text-gray-500 mt-1">Everyone across your recruitment pipelines.</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setModalOpen(true)} data-testid="new-candidate-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Candidate
          </Button>
        </div>

        {tableMissing && (
          <Card className="border-amber-300 bg-amber-50" data-testid="crm-migration-notice">
            <CardContent className="p-6 text-sm text-amber-800">
              Recruitment tables not migrated yet — run <code className="px-1 bg-amber-100 rounded">2026-06-15-crm-phase2.sql</code> in the Supabase SQL Editor.
            </CardContent>
          </Card>
        )}

        {!tableMissing && (
          <>
            {/* Pipeline summary */}
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
              {PIPELINE_STAGES.map((s) => (
                <Card key={s.value} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setStageFilter(s.value)} data-testid={`stage-summary-${s.value}`}>
                  <CardContent className="p-3 text-center">
                    <p className="text-xl font-bold text-gray-900">{counts[s.value] || 0}</p>
                    <p className="text-[11px] text-gray-500 flex items-center justify-center gap-1">
                      <span className={`w-2 h-2 rounded-full ${s.dot}`} />{s.label}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input className="pl-9" placeholder="Search candidates…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="candidates-search-input" />
              </div>
              <div className="flex gap-1 flex-wrap">
                {STAGE_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setStageFilter(f.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      stageFilter === f.key ? 'bg-blue-600 text-white' : 'bg-white border text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`candidates-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50/60 text-left text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-4 py-3 font-medium">Candidate</th>
                        <th className="px-4 py-3 font-medium">Job</th>
                        <th className="px-4 py-3 font-medium">Account</th>
                        <th className="px-4 py-3 font-medium">Current Role</th>
                        <th className="px-4 py-3 font-medium">Stage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>}
                      {!loading && filtered.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                          {candidates.length === 0 ? 'No candidates yet — add one from a job.' : 'No candidates match your filters.'}
                        </td></tr>
                      )}
                      {filtered.map((c) => (
                        <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50/60 transition-colors" data-testid={`candidate-row-${c.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold shrink-0">
                                {(c.first_name?.[0] || '') + (c.last_name?.[0] || '')}
                              </div>
                              <span className="font-medium text-gray-900">{candidateName(c)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <button className="text-blue-600 hover:underline" onClick={() => navigate(`/crm-jobs/${c.job_id}`)} data-testid={`candidate-job-link-${c.id}`}>
                              {c.jobTitle || '—'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{c.accountName || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{[c.current_title, c.current_company].filter(Boolean).join(' · ') || '—'}</td>
                          <td className="px-4 py-3">
                            <Select value={c.stage} onValueChange={(v) => moveStage(c, v)}>
                              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`candidate-stage-select-${c.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {PIPELINE_STAGES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <CandidateModal open={modalOpen} onOpenChange={setModalOpen} onSaved={load} />
      <PlacementModal
        open={placementModal.open}
        onOpenChange={(o) => setPlacementModal((s) => ({ ...s, open: o }))}
        onSaved={load}
        defaultJobId={placementModal.candidate?.job_id}
        defaultJobTitle={placementModal.candidate?.jobTitle}
        defaultCandidate={placementModal.candidate}
      />
    </SalesLayout>
  );
}
